import React, { useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import { useAppDispatch } from "../../store";
import Sidebar from "../../components/Sidebar";
import { Stepper, Step, StepLabel, Alert } from "@mui/material";
import ProfileStep from "./components/ProfileStep";
import QuestionsStep from "./components/QuestionsStep";
import ResultsStep from "./components/ResultsStep";
import type { Question, SubmitResponse } from "./types";
import "../Dashboard.scss";
import styles from "./SkillGapAssessment.module.scss";

const STEPS = ["Profile", "Questions", "Results"];

export default function SkillGapAssessment(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  const [stepIndex, setStepIndex] = useState(0);
  const [workflowId, setWorkflowId] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [result, setResult] = useState<SubmitResponse | null>(null);
  const [error, setError] = useState("");

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      setError("Logout failed. Please try again.");
    }
  };

  const handleStart = (wfId: string, qs: Question[]) => {
    setWorkflowId(wfId);
    setQuestions(qs);
    setError("");
    setStepIndex(1);
  };

  const handleSubmit = (submitResult: SubmitResponse) => {
    setResult(submitResult);
    setError("");
    setStepIndex(2);
  };

  const handleReset = () => {
    setStepIndex(0);
    setWorkflowId("");
    setQuestions([]);
    setResult(null);
    setError("");
  };

  const handleError = (errorMsg: string) => {
    setError(errorMsg);
  };

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="skill-gap"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <h1 className="page-title">Skill Gap Assessment</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            {error && (
              <Alert severity="error" className={styles.errorBanner} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <div className={styles.stepperContainer}>
              <Stepper activeStep={stepIndex}>
                {STEPS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </div>

            {stepIndex === 0 && (
              <ProfileStep onStart={handleStart} onError={handleError} />
            )}
            {stepIndex === 1 && (
              <QuestionsStep
                workflowId={workflowId}
                questions={questions}
                onSubmit={handleSubmit}
                onError={handleError}
              />
            )}
            {stepIndex === 2 && result && (
              <ResultsStep result={result} onReset={handleReset} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
